import React, { useState, useEffect, useContext, useMemo } from "react";
import styled from "styled-components";
import  {ConnectedLineChart as NVLineChart}  from "common/graphs/ConnectedLineChart";
import { isInsideRange, debounce, getSampleSize } from "common/api";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import { useScreenSize } from "common/hooks";
import { getInRangeItems, TileContainer } from "./utils";
import {formats} from "common/graphs/NVStackedAreaChart";

const ChartContainer = styled.div`
  height: 100%;

  .graph {
    height: 100%;
  }
  .border {
    position: absolute;
    background-color: transparent;

    &.top {
      width: 100%;
      height: 45px;
      top: 0;
      left: 0;
    }

    &.right {
      width: 40px;
      height: 100%;
      top: 0;
      right: 0;
    }

    &.bottom {
      width: 100%;
      height: 30px;
      bottom: 0;
      left: 0;
    }

    &.left {
      width: 60px;
      height: 100%;
      top: 0;
      left: 0;
    }
  }
`;

const oneDecimalWithPercentage = (d) =>
  d === undefined || d === null ? undefined : `${formats.oneDecimal(d)}`;

const congestionGraphSpecs = [
  {
    title: "Average traffic under congestion and with ACM",
    fields: [
      { name: "congested", label: "Congested-traffic-%" },
      { name: "acm", label: "ACM-%" },
    ],
    yAxisUnits: "% of total traffic",
    yTooltipFormat: oneDecimalWithPercentage,
    result: "congestion",
  },
  {
    title: "Latency Over Time",
    fields: [{ name: "Rtt-Access", label: "Latency-ms" }],
    yAxisUnits: "milliseconds",
    yTooltipFormat: undefined,
    result: "latency",
  },
  {
    title: "Average TCP Retransmissions Over Time",
    fields: [{ name: "Rtx%-Access", label: "Retransmissions-%" }],
    yAxisUnits: "retransmissions-%",
    yTooltipFormat: undefined,
    result: "retransmission",
  },
  {
    title: "Total Throughput",
    fields: [
      { name: "Dn", label: "Mbps-downlink" },
      { name: "Up", label: "Mbps-Uplink" },
    ],
    yAxisUnits: "Mbps",
    yTooltipFormat: undefined,
    result: "throughput",
  },
];

const Chart = ({
  range,
  interactionData,
  setInteractionData,
  title,
  fields,
  items,
  yAxisUnits,
  yTooltipFormat,
  hours,
  windowWidth,
}) => {
  return (
    <TileContainer key={title} className="title-and-graph">
      <h4 className="title">{title}</h4>
      <ChartContainer>
        <NVLineChart
          items={items}
          xField="time"
          className="graph"
          yAxisUnits={yAxisUnits}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          yTooltipFormat={yTooltipFormat}
          fields={fields}
          yAxisTopGap={0.05}
          fixedYRange={[0, 1]}
          onHighlight={(event) => {
            if (event === null) {
              setInteractionData(null);
            } else {
              setInteractionData(event);
            }
          }}
          highlight={interactionData}
          tooltipEnabled={true}
        />
        {interactionData !== null ? (
          <>
            <div
              className="border top"
              onMouseEnter={() => setInteractionData(null)}
              onMouseLeave={() => setInteractionData(null)}
            ></div>
            <div
              className="border right"
              onMouseEnter={() => setInteractionData(null)}
              onMouseLeave={() => setInteractionData(null)}
            ></div>
            <div
              className="border bottom"
              onMouseEnter={() => setInteractionData(null)}
              onMouseLeave={() => setInteractionData(null)}
            ></div>
            <div
              className="border left"
              onMouseEnter={() => setInteractionData(null)}
              onMouseLeave={() => setInteractionData(null)}
            ></div>
          </>
        ) : null}
      </ChartContainer>
    </TileContainer>
  );
};

const hoursPeriodMap = {
  24: "Day",
  168: "Week",
  720: "Month",
  2160: "ThreeMonths"
};

const Charts = (props) => {
  const { width: windowWidth } = useScreenSize();
  const POINTS_PER_TILE_CHART = windowWidth / 2;
  const [interactionData, setInteractionData] = useState(null);
  const { congestion, latency, retransmission, throughput, range, hours } =
    props;
  const sampleSize = range
    ? getSampleSize(range, POINTS_PER_TILE_CHART, hoursPeriodMap[hours])
    : 1;

  return (
    <>
      {congestionGraphSpecs.map(
        ({ title, fields, result, yAxisUnits, yTooltipFormat }) => {
          const inRangeItems = range
            ? getInRangeItems(props[result].items, range, fields, sampleSize)
            : [];
          return (
            <Chart
              key={`chart-${result}`}
              title={title}
              fields={fields}
              hours={hours}
              items={inRangeItems}
              yAxisUnits={yAxisUnits}
              yTooltipFormat={yTooltipFormat}
              range={range}
              interactionData={interactionData}
              setInteractionData={setInteractionData}
              windowWidth={windowWidth}
            />
          );
        }
      )}
    </>
  );
};

export default Charts;